import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Building2, MapPin, Phone, Mail, Pencil, X, Check } from 'lucide-react'
import { PageHero, PdfTile, Button, Input } from '@components/ui'
import { companyService } from '@services/companyService'
import { usePdfFiles } from '@hooks/useFileUploads'
import { useAuthStore } from '@store/useAuthStore'
import { FileUpload } from '../types/fileUpload'
import { userService } from '@services/userService'

const isWorkshopDocument = (document: FileUpload) => {
  const entityType = document.entityType?.toLowerCase()

  // Excluir explicitamente folhas de obra, clientes e orçamentos
  // Estes documentos já têm as suas próprias páginas
  if (
    entityType?.includes('work_order') ||
    entityType?.includes('workorder') ||
    entityType?.includes('quote') ||
    entityType?.includes('client') ||
    entityType?.includes('customer')
  ) {
    return false
  }

  // Apenas incluir documentos explicitamente da oficina
  if (entityType?.includes('workshop') || entityType?.includes('company') || entityType?.includes('oficina')) {
    return true
  }

  const tags = (document.tagsArray ?? [])
    .concat(document.tags ? document.tags.split(',') : [])
    .map((tag) => tag.toLowerCase().trim())

  // Verificar tags, mas excluir se tiver tags de folha de obra ou cliente
  const hasWorkOrderTags = tags.some((tag) => tag.includes('folha') || tag.includes('obra') || tag.includes('work'))
  const hasClientTags = tags.some((tag) => tag.includes('cliente') || tag.includes('customer'))
  
  if (hasWorkOrderTags || hasClientTags) {
    return false
  }

  // Apenas incluir se tiver tags explícitas de oficina/empresa
  return tags.some((tag) => tag.includes('oficina') || tag.includes('empresa') || tag.includes('dados'))
}

export function WorkshopPage() {
  const [search, setSearch] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const workshopCompanyId = user?.workshopCompanyId
  const isAdmin = (user?.functionId ?? 0) >= 3

  const { data: workshop, isLoading: isCompanyLoading } = useQuery(
    ['workshop-company', workshopCompanyId],
    () => companyService.getDetails(workshopCompanyId!),
    { enabled: !!workshopCompanyId }
  )

  const { data: entryLevel } = useQuery(['entry-level-me', user?.id], () => userService.getEntryLevelStatus(), {
    enabled: !!user?.id,
    retry: false,
  })

  const updateMutation = useMutation(
    (data: { name: string; address: string; idAdmin: string; nif?: string; phone?: string }) =>
      companyService.update(workshopCompanyId!, {
        ...data,
        type: 'OFICINA',
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['workshop-company', workshopCompanyId])
        setIsEditing(false)
      },
    }
  )

  const pdfQueryParams = useMemo(() => ({ entityType: 'workshop' }), [])

  const { data: documents = [], isLoading: isDocsLoading } = usePdfFiles(pdfQueryParams)

  const workshopDocuments = useMemo(() => {
    // Filtrar apenas documentos realmente da oficina (excluir folhas de obra e clientes)
    return documents.filter(isWorkshopDocument)
  }, [documents])

  const filteredDocuments = useMemo(() => {
    const searchTerm = search.trim().toLowerCase()

    if (!searchTerm) {
      return workshopDocuments
    }

    return workshopDocuments.filter((document) => {
      const haystack = [
        document.originalFilename,
        document.description,
        document.tags,
        document.tagsArray?.join(' '),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return haystack.includes(searchTerm)
    })
  }, [search, workshopDocuments])

  // Remover fallback documents - apenas mostrar documentos reais da oficina

  const emptyLabel = '—'
  const workshopName = workshop?.name?.trim() || emptyLabel
  const workshopAddress = workshop?.address?.trim() || emptyLabel
  const workshopManager = workshop?.idAdmin?.trim() || emptyLabel
  const workshopNif = workshop?.nif?.trim() || emptyLabel
  const workshopContact = workshop?.phone?.trim() || emptyLabel
  const planCode = (entryLevel?.code ?? '').toLowerCase()
  const planLabel =
    entryLevel?.name?.trim() ||
    (planCode === 'basic'
      ? 'FIXDAUTO Base'
      : planCode === 'pro' || planCode === 'premium'
        ? 'FIXDAUTO Pro'
        : planCode === 'freemium'
          ? 'Freemium'
          : entryLevel?.code
            ? entryLevel.code
            : '—')
  const isFreemium = planCode === 'freemium'
  const daysRemaining = entryLevel?.daysRemaining
  const deadlineAt = entryLevel?.deadlineAt
  const deadlineDate =
    deadlineAt && !Number.isNaN(Date.parse(deadlineAt))
      ? new Date(deadlineAt).toLocaleDateString('pt-PT')
      : null

  const [editForm, setEditForm] = useState({
    name: '',
    address: '',
    idAdmin: '',
    nif: '',
    phone: '',
  })

  const startEditing = () => {
    if (workshop) {
      setEditForm({
        name: workshop.name || '',
        address: workshop.address || '',
        idAdmin: workshop.idAdmin || '',
        nif: workshop.nif || '',
        phone: workshop.phone || '',
      })
      setIsEditing(true)
    }
  }

  const cancelEditing = () => {
    setIsEditing(false)
  }

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault()
    updateMutation.mutate({
      name: editForm.name.trim(),
      address: editForm.address.trim(),
      idAdmin: editForm.idAdmin.trim(),
      nif: editForm.nif.trim() || undefined,
      phone: editForm.phone.trim() || undefined,
    })
  }

  const openDocument = (doc: FileUpload) => {
    if (!doc) {
      alert('Erro: Documento não encontrado')
      return
    }

    const url = doc.viewUrl || doc.downloadUrl
    if (!url) {
      alert('Este documento não possui URL de visualização disponível.')
      return
    }
    const w = window.open(url, '_blank', 'noopener,noreferrer')
    if (!w) {
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      a.rel = 'noopener noreferrer'
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-200px)] flex-col">
      <PageHero
        title="DADOS DA OFICINA"
        searchPlaceholder="Pesquisar documentos da oficina..."
        searchValue={search}
        onSearchChange={setSearch}
        className="mb-6 md:mb-8"
      />

      <section className="flex flex-1 flex-col rounded-[32px] border border-secondary-200 bg-white p-4 sm:p-6 md:p-8 lg:p-12 xl:p-16">
        {!workshopCompanyId ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-lg md:text-xl lg:text-2xl text-secondary-500">Sem oficina associada ao seu utilizador.</p>
          </div>
        ) : isCompanyLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-lg md:text-xl lg:text-2xl text-secondary-500">A carregar dados da oficina...</p>
          </div>
        ) : isEditing ? (
          <form onSubmit={handleSaveEdit} className="flex flex-1 flex-col gap-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <h2 className="text-lg font-semibold text-secondary-900">Editar dados da oficina</h2>
              <div className="flex gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={cancelEditing} disabled={updateMutation.isLoading}>
                  <X className="h-4 w-4 mr-1 inline" />
                  Cancelar
                </Button>
                <Button type="submit" size="sm" isLoading={updateMutation.isLoading} disabled={updateMutation.isLoading}>
                  <Check className="h-4 w-4 mr-1 inline" />
                  Guardar
                </Button>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Nome"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nome da oficina"
                required
              />
              <Input
                label="Morada"
                value={editForm.address}
                onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="Morada"
                required
              />
              <Input
                label="NIF"
                value={editForm.nif}
                onChange={(e) => setEditForm((f) => ({ ...f, nif: e.target.value }))}
                placeholder="NIF (opcional)"
              />
              <Input
                label="Telefone"
                value={editForm.phone}
                onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="Telefone (opcional)"
              />
              <Input
                label="Email / ID Administrador"
                value={editForm.idAdmin}
                onChange={(e) => setEditForm((f) => ({ ...f, idAdmin: e.target.value }))}
                placeholder="Email ou identificador do administrador"
                required
              />
            </div>
          </form>
        ) : (
          <div className="flex flex-1 flex-col gap-6 sm:gap-8">
            {isAdmin ? (
              <div className="flex shrink-0 justify-end">
                <Button variant="outline" size="sm" onClick={startEditing} className="shrink-0">
                  <Pencil className="h-4 w-4 mr-1 inline" />
                  Editar dados
                </Button>
              </div>
            ) : null}
            <div className="flex flex-col gap-6 sm:gap-8 lg:flex-row lg:items-center lg:justify-center lg:gap-12 xl:gap-16">
              {/* Imagem - sempre em cima em mobile/tablet, lateral em desktop */}
              <div className="flex w-full items-center justify-center mb-2 sm:mb-4 lg:mb-0 lg:w-auto lg:flex-shrink-0">
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-secondary-200 sm:h-32 sm:w-32 md:h-40 md:w-40 lg:h-56 lg:w-56 xl:h-64 xl:w-64">
                  <Building2 className="h-8 w-8 text-secondary-700 sm:h-12 sm:w-12 md:h-16 md:w-16 lg:h-24 lg:w-24 xl:h-28 xl:w-28" />
                </div>
              </div>

              <div className="grid min-w-0 flex-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:gap-8">
              <div className="space-y-2 mb-4">
                <span className="text-xs sm:text-sm md:text-base font-semibold uppercase tracking-[0.3em] text-secondary-400">
                  Nome
                </span>
                <p className="text-xl sm:text-2xl md:text-3xl font-semibold text-secondary-900 break-words leading-tight">
                  {workshopName}
                </p>
              </div>

              <div className="space-y-2 mb-4">
                <span className="text-xs sm:text-sm md:text-base font-semibold uppercase tracking-[0.3em] text-secondary-400">
                  Morada
                </span>
                <div className="flex items-start gap-2 text-secondary-700">
                  <MapPin className="h-5 w-5 text-primary-600 flex-shrink-0 mt-1" />
                  <p className="text-base sm:text-lg font-medium break-words leading-relaxed">
                    {workshopAddress}
                  </p>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <span className="text-xs sm:text-sm md:text-base font-semibold uppercase tracking-[0.3em] text-secondary-400">
                  NIF
                </span>
                <p className="text-lg sm:text-xl md:text-2xl font-semibold tracking-[0.2em] text-secondary-900">
                  {workshopNif}
                </p>
              </div>

              <div className="space-y-2 mb-4">
                <span className="text-xs sm:text-sm md:text-base font-semibold uppercase tracking-[0.3em] text-secondary-400">
                  Contactos
                </span>
                <div className="flex items-center gap-2 text-secondary-700 mb-2">
                  <Phone className="h-5 w-5 text-primary-600 flex-shrink-0" />
                  <p className="text-base sm:text-lg font-medium">
                    {workshopContact}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-secondary-500">
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  <span className="text-xs sm:text-sm uppercase tracking-[0.25em]">
                    {workshopManager}
                  </span>
                </div>
              </div>

              <div className="space-y-2 mb-4 sm:col-span-2">
                <span className="text-xs sm:text-sm md:text-base font-semibold uppercase tracking-[0.3em] text-secondary-400">
                  Plano
                </span>
                <p className="text-base sm:text-lg font-semibold text-secondary-900">
                  {planLabel}
                  {isFreemium && typeof daysRemaining === 'number'
                    ? ` — ${daysRemaining} dias restantes`
                    : null}
                </p>
                {isFreemium && deadlineDate ? (
                  <p className="text-sm text-secondary-500">
                    Acesso freemium até {deadlineDate}.
                  </p>
                ) : null}
              </div>
            </div>
            </div>
          </div>
        )}
      </section>

      {/* Mostrar secção de documentos apenas se houver documentos da oficina (não folhas de obra ou clientes) */}
      {!isDocsLoading && filteredDocuments.length > 0 && (
        <section className="mt-8 space-y-4 md:space-y-6">
          <h2 className="text-sm sm:text-base md:text-lg font-semibold uppercase tracking-[0.3em] text-secondary-500">
            Documentos PDF da Oficina
          </h2>
          <div className="grid gap-4 sm:gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {filteredDocuments.slice(0, 12).map((item) => {
              // Verificar se o documento tem URL para download/view
              const hasUrl = !!(item.viewUrl || item.downloadUrl)

              return (
                <PdfTile
                  key={item.id}
                  label={
                    item.description?.trim() ||
                    item.originalFilename.replace(/\.[^/.]+$/, '') ||
                    `Documento ${item.id}`
                  }
                  onClick={hasUrl ? () => openDocument(item) : undefined}
                  className={hasUrl ? 'hover:scale-105 cursor-pointer' : 'opacity-50 cursor-not-allowed'}
                />
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}

